import { useState, useEffect, useContext } from "react";
import { Table, Typography, Input, Form, Tooltip, Tag, Select } from "antd";
import { OverlayContext } from "../../context/OverlayContext";
import { ToastContext } from "../../context/ToastContext";
import BookingForm from "../forms/BookingForm";
import Booking from "../../models/Booking";
import ResourceService from "../../services/crud/ResourceService";
import HubService from "../../services/crud/HubService";
import { listToMap } from "../../helpers/array-to-map";
import { TableProps } from "./types";
import BookingExternalService from "../../services/crud/BookingExternalService";
import { ColumnType } from "antd/lib/table";
import { formatDateTime } from "../../utils/dates";
import { capitalizeFirstLetterLowerRest } from "../../helpers/string";
import { BookingExternalListRow } from "../../models/BookingsExternalListRow";
import TspService from "../../services/crud/TspService";
import Tsp from "../../models/Tsp";
import { CarOutlined, ShopOutlined } from "@ant-design/icons";
import Hub from "../../models/Hub";

/**
 * This table is used to represent a table in database.
 * @param {props} - dataFilter to choose which data to keep. ColumnFilter which columns to display. Default is no filter.
 * @returns table populated with bookings data
 */
export interface BookingTableData {
  booking: BookingExternalListRow;
  totalPrice: string;
  bookedTo: string;
  bookedFrom: string;
  createdAt: string;
}

const mapBookingToTableData = (data: BookingExternalListRow): BookingTableData => {
  return {
    booking: data,
    totalPrice: data.totalPriceInKr + ' kr',
    bookedFrom: formatDateTime(data.bookFromDatetime),
    bookedTo: formatDateTime(data.bookToDatetime),
    createdAt: formatDateTime(data.createdAt),
  };
};

function Bookings({ dataFilter = (data: BookingTableData) => true, columnFilter = (col) => true }: TableProps<BookingTableData>) {
  const [bookings, setBookings] = useState<Array<BookingTableData>>([]);
  const [tsps, setTsps] = useState<Array<Tsp>>([]);
  const [hubs, setHubs] = useState<Array<Hub>>([]);
  const [textFilter, setTextFilter] = useState("");
  const [tspFilter, setTspFilter] = useState<number|null>(null);
  const [hubFilter, setHubFilter] = useState<number|null>(null);
  const { openModal } = useContext(OverlayContext);
  const { loading, loadMessage } = useContext(ToastContext);
  const [form] = Form.useForm();

  useEffect(() => {
    loadMessage("Loading Bookings...", async () => {
      const bookingPromise = BookingExternalService.list().then((data) => {
          const loadedBookings = data.map(mapBookingToTableData).filter(dataFilter);
          loadedBookings.sort((a: any, b: any) => -1 * `${a.bookedFrom} ${a.bookedTo}`.localeCompare(`${b.bookedFrom} ${b.bookedTo}`));
          setBookings(loadedBookings);
      });

      // Tsp and Hubs are only used by filter and is fine if they take longer to load than the table data.
      TspService.list().then((loadedTsps) => {
        loadedTsps.sort((a: Tsp, b: Tsp) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
        setTsps(loadedTsps);
      });
      HubService.list().then((loadedHubs) => {
        loadedHubs.sort((a: Hub, b: Hub) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
        setHubs(loadedHubs);
      });

      await bookingPromise;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openViewModal = (bookingId: number) => {
    openModal({
      title: "View booking",
      content: <BookingForm setBookings={setBookings} bookingId={bookingId} />,
      okText: "Close",
      hasCancel: false,
    });
  };

  const columns: ColumnType<BookingTableData>[] = [
    {
      title: "ID",
      key: "id", // Used by UserProfile via columnFilter prop
      dataIndex: ["id"],
      render: (text, record, index) => {
        return (
          <div>
          {record.booking.id}
          </div>
        );
      },
      sorter: (a, b) => b.booking.id - a.booking.id,
    },
    {
      title: "User",
      key: "userId", // Used by UserProfile via columnFilter prop
      dataIndex: ["user"],
      render: (text, record, index) => {
        return (
          <Tooltip title={`${record?.booking.userUsername ?? ""} ${record?.booking.userLastName ?? ""}`.trim()}>
            {record?.booking.userUsername ?? `user id: ${record?.booking.userId}`}
          </Tooltip>
        );
      },
      sorter: (a, b) =>
        (a.booking.userUsername?.toLowerCase() ?? a.booking.userId.toString()).localeCompare(
          b.booking.userUsername?.toLowerCase() ?? b.booking.userId?.toString(),
        ),
    },
    {
      title: "Resource",
      key: "resourceId", // Used by ResourceView via columnFilter prop
      dataIndex: ["booking", "bookingLabel"],
      render: (text, record, index) => {
        return <Tooltip title={record?.booking.bookingLabel}>{record?.booking.bookingLabel}</Tooltip>;
      },
      sorter: (a, b) => (a.booking.bookingLabel?.toLowerCase() ?? "").localeCompare(b.booking.bookingLabel?.toLowerCase()),
    },
    {
      title: "Hub",
      key: "pickupHubId", // Used by HubView via columnFilter prop
      dataIndex: ["booking", "pickupHubName"],
      render: (text, record, index) => {
        return text ?? "";
      },
      sorter: (a, b) => (a.booking.pickupHubName?.toLowerCase() ?? "").localeCompare(b.booking.pickupHubName?.toLowerCase() ?? ""),
    },
    {
      title: "Price",
      dataIndex: "price",
      render: (text, record, index) => {
        return `${record.totalPrice}`;
      },
      sorter: (a, b) => a.totalPrice.localeCompare(b.totalPrice),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text, record, index) => {
        return (
          <>
            <Tag color={Booking.STATUS_COLORS[record.booking.status.toLowerCase()]}>
              {capitalizeFirstLetterLowerRest(record.booking.status)}
            </Tag>
            {record.booking.isLate && <Tag color="red">Late</Tag>}
          </>
        );
      },
      sorter: (a, b) => a.booking.status.toLowerCase().localeCompare(b.booking.status.toLowerCase()),
    },
    {
      title: "Bokad tid",
      dataIndex: "bookedFrom",
      render: (text, record, index) => {
        return (
          <>
            <span aria-label="from">{record.bookedFrom}</span>
            <span aria-hidden="true"> - </span>
            <span
              aria-label="to"
              style={{color: record.booking.isLate ? 'red': undefined}}
            >{record.bookedTo}</span>
          </>
        )
      },
      sorter: (a, b) => `${a.bookedFrom} ${a.bookedTo}`.localeCompare(`${b.bookedFrom} ${b.bookedTo}`),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      render: (text, record, index) => {
        return `${record.createdAt}`;
      },
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
    },
  ];
  // Filters
  const filterColumns = (columns = []) => columns.filter(columnFilter);
  const filterTable = (data) => data.filter((item: BookingTableData) => {
    let textFilterMatch = true;
    if (textFilter.trim() !== '') {
      const itemData = [
        item.booking.id.toString(),
        (item.booking.tspBookingId ?? '').toString(),
        item.booking.bookingLabel ?? '',
        item.booking.pickupHubName ?? '',
        (item.booking.userFirstName ?? '') + ' ' + (item.booking.userLastName ?? ''),
        (item.booking.userUsername ?? ''),
      ]
      textFilterMatch = itemData
        .map((itemPart) => (itemPart as String).toLowerCase().indexOf(textFilter.toLowerCase()) !== -1)
        .reduce((any, value) => any || value, false);
    }
    const tspFilterMatch = tspFilter === null || item.booking.tspId === tspFilter;
    const hubFilterMatch = hubFilter === null || item.booking.pickupHubId === hubFilter;
    return textFilterMatch && tspFilterMatch && hubFilterMatch;
  });
  const showHubsFilter = filterColumns(columns).find((col) => col.key === "pickupHubId") !== undefined;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <Input.Search
            allowClear
            size="middle"
            placeholder="Search bookings..."
            style={{ width: 200 }}
            onChange={(e) => setTextFilter(e.target.value)}
          />
          <span style={{display: 'inline-block', width: '10px'}} />
          <Select
            allowClear
            defaultValue={[]}
            options={tsps.map((tsp) => ({value: tsp.id, label: tsp.name}))}
            onChange={(value) => setTspFilter((value ?? null) as any)}
            placeholder="TSP"
            suffixIcon={<CarOutlined/>}
            style={{width: 200}}
          />
          { showHubsFilter && (<>
            <span style={{display: 'inline-block', width: '10px'}} />
            <Select
              allowClear
              defaultValue={[]}
              options={hubs.map((hub) => ({value: hub.id, label: hub.name}))}
              onChange={(value) => setHubFilter((value ?? null) as any)}
              placeholder="Hub"
              suffixIcon={<ShopOutlined/>}
              style={{width: 300}}
            />
          </>)}
        </div>
        {/* <Button type="primary" size="middle" onClick={() => openCreateModal()}>
          Create new booking
        </Button> */}
      </div>
      <Table
        rowKey="id"
        columns={filterColumns(columns)}
        dataSource={filterTable(bookings)}
        onRow={(record: BookingTableData) => ({ onClick: () => openViewModal(record.booking.id) })}
        pagination={{ total: bookings.length, defaultPageSize: 30, showSizeChanger: true }}
        loading={loading}
      />
      <Typography.Text style={{ position: "relative", top: -43, left: 10, color: "#bfbfbf" }}>Total of {bookings.length} bookings</Typography.Text>
    </div>
  );
}

export default Bookings;
